import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";

const EVENT_SLUGS = ["studio", "grote-vergaderzaal"];

export default function Events() {
  const [rooms, setRooms] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/spaces");
        const all = res.data.spaces || [];
        const ordered = EVENT_SLUGS.map((s) => all.find((x) => x.slug === s)).filter(Boolean);
        setRooms(ordered);
      } catch (err) {
        console.error("Failed to load events spaces:", err);
      }
    })();
  }, []);

  return (
    <main data-testid="events-page">
      <Seo
        title="Eventlocatie & fotostudio in Roeselare — Noordstraat 140"
        description="Authentieke eventlocatie en fotostudio in het hart van Roeselare. Pop-up diners, lezingen, launches, foto- en filmshoots in een gerestaureerde koffiebranderij."
        canonical="https://www.noordstraat140.com/events"
      />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-10 pb-6">
        <Link
          to="/"
          className="overline inline-flex items-center gap-2 hover:text-[var(--accent)]"
          data-testid="back-home"
        >
          <ArrowLeft size={14} /> Terug naar home
        </Link>
      </div>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 md:col-span-7">
            <p className="overline" style={{ color: "var(--accent)" }}>Events</p>
            <h1
              className="font-serif font-light tracking-tighter leading-[0.95] mt-5"
              style={{ fontSize: "clamp(40px, 6.4vw, 96px)" }}
              data-testid="events-title"
            >
              Organiseer een
              <br />
              <em className="not-italic" style={{ color: "var(--accent)" }}>
                event.
              </em>
            </h1>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p
              className="text-base md:text-lg leading-relaxed"
              style={{ color: "var(--text-secondary)" }}
            >
              Twee opties voor je bijeenkomst. De studio voor een intieme
              dinner, een pop-up bar of een sfeervol fotoshoot-event. Of de
              grote vergaderzaal van 90 m² met beamer, geluid en vintage
              theaterstoelen voor presentaties, talks en workshops.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid grid-cols-12 gap-6 md:gap-10" data-testid="events-grid">
          {rooms.map((r) => (
            <Link
              key={r.slug}
              to={`/spaces/${r.slug}`}
              className="col-span-12 md:col-span-6 group block"
              data-testid={`event-card-${r.slug}`}
            >
              <div className="relative aspect-[5/4] overflow-hidden">
                <img
                  src={r.image}
                  alt={r.name}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute top-4 left-4">
                  <span
                    className="overline px-3 py-1.5"
                    style={{
                      backgroundColor: "var(--bg-base)",
                      color: "var(--text-primary)",
                    }}
                  >
                    {r.slug === "studio" ? "Studio & event space" : "Vergaderzaal & event"}
                  </span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:justify-between mt-5 gap-3 sm:gap-6">
                <div className="min-w-0">
                  <p className="font-serif text-3xl md:text-4xl font-light leading-tight">
                    {r.name}
                  </p>
                  <p
                    className="text-sm mt-1 italic"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {r.tagline}
                  </p>
                  <p
                    className="text-sm mt-3"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {r.capacity}
                  </p>
                </div>
                <div className="text-left sm:text-right sm:shrink-0">
                  <p className="overline" style={{ color: "var(--text-secondary)" }}>
                    Vanaf
                  </p>
                  <p className="font-serif text-base sm:text-lg mt-1">{r.price_from}</p>
                </div>
              </div>
              <p
                className="overline mt-6 inline-flex items-center gap-2 transition-colors group-hover:text-[var(--accent)]"
                style={{ color: "var(--text-primary)" }}
              >
                Bekijk & boek <ArrowUpRight size={14} />
              </p>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
